'use strict'

const KMZ_HEADING_SCOPE_PRIORITY = {
  playback: ['waylines', 'template'],
  templateExport: ['template', 'waylines'],
  waylinesExport: ['waylines', 'template']
}

const KMZ_HEADING_LOCAL_CHILDREN = [
  'waypointHeadingMode',
  'waypointHeadingAngle',
  'waypointPoiPoint',
  'waypointHeadingAngleEnable',
  'waypointHeadingPathMode',
  'waypointHeadingPoiIndex'
]

const KMZ_HEADING_GLOBAL_CHILDREN = [
  'waypointHeadingMode',
  'waypointHeadingAngle',
  'waypointPoiPoint',
  'waypointHeadingPoiIndex'
]

const KMZ_HEADING_ROOT_CACHE_PATHS = [
  'wpml:useGlobalHeadingParam',
  'useGlobalHeadingParam',
  'wpml:waypointHeadingParam',
  'waypointHeadingParam',
  'wpml:globalWaypointHeadingParam',
  'globalWaypointHeadingParam',
  'Folder.wpml:globalWaypointHeadingParam',
  'Folder.globalWaypointHeadingParam'
]

const KMZ_HEADING_SCOPED_CACHE_PATHS = [
  'wpml:useGlobalHeadingParam',
  'useGlobalHeadingParam',
  'wpml:waypointHeadingParam',
  'waypointHeadingParam',
  'wpml:globalWaypointHeadingParam',
  'globalWaypointHeadingParam',
  'Folder.wpml:globalWaypointHeadingParam',
  'Folder.globalWaypointHeadingParam'
]

const fnCloneHeadingValue = function (value) {
  if (Array.isArray(value)) {
    return value.map(fnCloneHeadingValue)
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = fnCloneHeadingValue(value[key])
    })
    return out
  }
  return value
}

const fnGetByPath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return undefined
    current = current[parts[i]]
  }
  return current
}

const fnSetByPath = function (obj, path, value) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  let current = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
      current[key] = {}
    }
    current = current[key]
  }
  current[parts[parts.length - 1]] = fnCloneHeadingValue(value)
}

const fnDeletePath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  const stack = [{ parent: null, key: '', value: obj }]
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return
    const key = parts[i]
    if (!Object.prototype.hasOwnProperty.call(current, key)) return
    current = current[key]
    stack.push({ parent: stack[stack.length - 1].value, key: key, value: current })
  }
  const leaf = stack.pop()
  if (!leaf || !leaf.parent || !leaf.key) return
  delete leaf.parent[leaf.key]
  while (stack.length > 1) {
    const node = stack.pop()
    if (!node || !node.parent || !node.key) continue
    const value = node.parent[node.key]
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length) break
    delete node.parent[node.key]
  }
}

const fnPickFirstDefined = function (source, paths) {
  const safeSource = (source && typeof source === 'object') ? source : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const path = String(list[i] || '').trim()
    if (!path) continue
    const value = fnGetByPath(safeSource, path)
    if (value === undefined) continue
    return value
  }
  return undefined
}

const fnAssignScopedHeadingSource = function (target, targetPath, source, sourcePaths) {
  const value = fnPickFirstDefined(source, sourcePaths)
  if (value === undefined) return
  fnSetByPath(target, targetPath, value)
}

const fnBuildScopeHeadingSource = function (pointProps, folderProps) {
  const out = {}
  fnAssignScopedHeadingSource(out, 'wpml:useGlobalHeadingParam', pointProps, [
    'wpml:useGlobalHeadingParam',
    'useGlobalHeadingParam'
  ])
  fnAssignScopedHeadingSource(out, 'wpml:waypointHeadingParam', pointProps, [
    'wpml:waypointHeadingParam',
    'waypointHeadingParam'
  ])
  fnAssignScopedHeadingSource(out, 'Folder.wpml:globalWaypointHeadingParam', pointProps, [
    'Folder.wpml:globalWaypointHeadingParam',
    'Folder.globalWaypointHeadingParam',
    'wpml:globalWaypointHeadingParam',
    'globalWaypointHeadingParam'
  ])
  fnAssignScopedHeadingSource(out, 'Folder.wpml:globalWaypointHeadingParam', folderProps, [
    'wpml:globalWaypointHeadingParam',
    'globalWaypointHeadingParam',
    'Folder.wpml:globalWaypointHeadingParam',
    'Folder.globalWaypointHeadingParam'
  ])
  return out
}

const fnPruneKmzHeadingCache = function (props) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return
  KMZ_HEADING_ROOT_CACHE_PATHS.forEach((path) => {
    fnDeletePath(safeProps, path)
  })
  ;['template', 'waylines'].forEach((scopeId) => {
    KMZ_HEADING_SCOPED_CACHE_PATHS.forEach((path) => {
      fnDeletePath(safeProps, scopeId + '.' + path)
    })
  })
}

const buildKmzScopedHeadingPropsFromSources = function ({
  templatePointProps,
  waylinePointProps,
  templateFolderProps,
  waylinesFolderProps
} = {}) {
  return {
    template: fnBuildScopeHeadingSource(templatePointProps, templateFolderProps),
    waylines: fnBuildScopeHeadingSource(waylinePointProps, waylinesFolderProps)
  }
}

const applyKmzScopedHeadingProps = function (props, scopedHeadingProps) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return safeProps
  const scoped = (scopedHeadingProps && typeof scopedHeadingProps === 'object') ? scopedHeadingProps : {}
  fnPruneKmzHeadingCache(safeProps)
  ;['template', 'waylines'].forEach((scopeId) => {
    const scopeProps = scoped[scopeId]
    if (!scopeProps || typeof scopeProps !== 'object') return
    fnAssignScopedHeadingSource(safeProps, scopeId + '.wpml:useGlobalHeadingParam', scopeProps, [
      'wpml:useGlobalHeadingParam',
      'useGlobalHeadingParam'
    ])
    fnAssignScopedHeadingSource(safeProps, scopeId + '.wpml:waypointHeadingParam', scopeProps, [
      'wpml:waypointHeadingParam',
      'waypointHeadingParam'
    ])
    fnAssignScopedHeadingSource(safeProps, scopeId + '.Folder.wpml:globalWaypointHeadingParam', scopeProps, [
      'Folder.wpml:globalWaypointHeadingParam',
      'Folder.globalWaypointHeadingParam',
      'wpml:globalWaypointHeadingParam',
      'globalWaypointHeadingParam'
    ])
  })
  return safeProps
}

const syncKmzPointHeadingScopesFromXml = function (point, {
  fnXmlToObj,
  templateFolderProps,
  waylinesFolderProps,
  templateFolderEl,
  waylinesFolderEl
} = {}) {
  if (!point || typeof fnXmlToObj !== 'function') return point
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const templatePointProps = point.xmlEl ? fnXmlToObj(point.xmlEl) : {}
  const waylinePointEl = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const waylinePointProps = waylinePointEl ? fnXmlToObj(waylinePointEl) : {}
  const scoped = buildKmzScopedHeadingPropsFromSources({
    templatePointProps: templatePointProps,
    waylinePointProps: waylinePointProps,
    templateFolderProps: templateFolderProps || (templateFolderEl ? fnXmlToObj(templateFolderEl) : {}),
    waylinesFolderProps: waylinesFolderProps || (waylinesFolderEl ? fnXmlToObj(waylinesFolderEl) : {})
  })
  applyKmzScopedHeadingProps(point.props, scoped)
  return point
}

const syncKmzMissionHeadingScopesFromXml = function (missionState, fnXmlToObj) {
  if (!missionState || !Array.isArray(missionState.waypoints) || typeof fnXmlToObj !== 'function') return missionState
  const templateFolderProps = missionState.folderEl ? fnXmlToObj(missionState.folderEl) : {}
  const waylinesFolderProps = missionState.secondaryFolderEl ? fnXmlToObj(missionState.secondaryFolderEl) : {}
  missionState.waypoints.forEach((point) => {
    syncKmzPointHeadingScopesFromXml(point, {
      fnXmlToObj: fnXmlToObj,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    })
  })
  return missionState
}

const fnNormalizeHeadingPathForCheck = function (path) {
  let normalized = String(path || '').trim()
  if (!normalized) return ''
  const lower = normalized.toLowerCase()
  if (lower.indexOf('template.') === 0) normalized = normalized.slice(9)
  if (String(normalized).toLowerCase().indexOf('waylines.') === 0) normalized = normalized.slice(9)
  return String(normalized || '').trim().toLowerCase()
}

const isKmzHeadingFieldPath = function (path) {
  const normalized = fnNormalizeHeadingPathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:useglobalheadingparam' ||
    normalized === 'useglobalheadingparam' ||
    normalized.indexOf('wpml:waypointheadingparam') === 0 ||
    normalized.indexOf('waypointheadingparam') === 0 ||
    normalized.indexOf('wpml:globalwaypointheadingparam') === 0 ||
    normalized.indexOf('globalwaypointheadingparam') === 0 ||
    normalized.indexOf('folder.wpml:globalwaypointheadingparam') === 0 ||
    normalized.indexOf('folder.globalwaypointheadingparam') === 0
}

const isKmzGlobalHeadingFieldPath = function (path) {
  const normalized = fnNormalizeHeadingPathForCheck(path)
  if (!normalized) return false
  return normalized.indexOf('wpml:globalwaypointheadingparam') === 0 ||
    normalized.indexOf('globalwaypointheadingparam') === 0 ||
    normalized.indexOf('folder.wpml:globalwaypointheadingparam') === 0 ||
    normalized.indexOf('folder.globalwaypointheadingparam') === 0
}

const fnResolveHeadingValueByUseCase = function (pointOrProps, useCase, mode, childName, fallbackValue) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const scopePriority = KMZ_HEADING_SCOPE_PRIORITY[useCase] || KMZ_HEADING_SCOPE_PRIORITY.playback
  const child = String(childName || '').trim()
  for (let i = 0; i < scopePriority.length; i += 1) {
    const scopeId = scopePriority[i]
    let paths = []
    if (mode === 'useGlobal') {
      paths = [
        scopeId + '.wpml:useGlobalHeadingParam',
        scopeId + '.useGlobalHeadingParam'
      ]
    } else if (mode === 'local' && child) {
      paths = [
        scopeId + '.wpml:waypointHeadingParam.wpml:' + child,
        scopeId + '.wpml:waypointHeadingParam.' + child,
        scopeId + '.waypointHeadingParam.wpml:' + child,
        scopeId + '.waypointHeadingParam.' + child
      ]
    } else if (mode === 'global' && child) {
      paths = [
        scopeId + '.Folder.wpml:globalWaypointHeadingParam.wpml:' + child,
        scopeId + '.Folder.wpml:globalWaypointHeadingParam.' + child,
        scopeId + '.Folder.globalWaypointHeadingParam.wpml:' + child,
        scopeId + '.Folder.globalWaypointHeadingParam.' + child,
        scopeId + '.wpml:globalWaypointHeadingParam.wpml:' + child,
        scopeId + '.wpml:globalWaypointHeadingParam.' + child,
        scopeId + '.globalWaypointHeadingParam.wpml:' + child,
        scopeId + '.globalWaypointHeadingParam.' + child
      ]
    }
    for (let j = 0; j < paths.length; j += 1) {
      const value = fnGetByPath(props, paths[j])
      if (value === undefined || value === null) continue
      if (typeof value === 'string' && !String(value).trim()) continue
      return value
    }
  }
  return fallbackValue
}

const resolveKmzPlaybackUseGlobalHeading = function (pointOrProps, fallbackValue) {
  return fnResolveHeadingValueByUseCase(pointOrProps, 'playback', 'useGlobal', '', fallbackValue)
}

const resolveKmzPlaybackLocalHeadingField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveHeadingValueByUseCase(pointOrProps, 'playback', 'local', childName, fallbackValue)
}

const resolveKmzPlaybackGlobalHeadingField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveHeadingValueByUseCase(pointOrProps, 'playback', 'global', childName, fallbackValue)
}

const resolveKmzTemplateExportUseGlobalHeading = function (pointOrProps, fallbackValue) {
  return fnResolveHeadingValueByUseCase(pointOrProps, 'templateExport', 'useGlobal', '', fallbackValue)
}

const resolveKmzTemplateExportHeadingField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveHeadingValueByUseCase(pointOrProps, 'templateExport', 'local', childName, fallbackValue)
}

const resolveKmzWaylinesExportUseGlobalHeading = function (pointOrProps, fallbackValue) {
  return fnResolveHeadingValueByUseCase(pointOrProps, 'waylinesExport', 'useGlobal', '', fallbackValue)
}

const resolveKmzWaylinesExportHeadingField = function (pointOrProps, childName, fallbackValue) {
  return fnResolveHeadingValueByUseCase(pointOrProps, 'waylinesExport', 'local', childName, fallbackValue)
}

const applyKmzResolvedHeadingDisplayValuesToMap = function (map, point, useCase) {
  const targetMap = (map && typeof map === 'object') ? map : {}
  const safeUseCase = String(useCase || 'playback').trim()
  const localResolver = function (childName) {
    return fnResolveHeadingValueByUseCase(point, safeUseCase, 'local', childName, undefined)
  }
  const globalResolver = function (childName) {
    return fnResolveHeadingValueByUseCase(point, safeUseCase, 'global', childName, undefined)
  }
  const useGlobalValue = fnResolveHeadingValueByUseCase(point, safeUseCase, 'useGlobal', '', undefined)
  if (useGlobalValue !== undefined) {
    targetMap['wpml:useGlobalHeadingParam'] = useGlobalValue
  }
  KMZ_HEADING_LOCAL_CHILDREN.forEach((childName) => {
    const value = localResolver(childName)
    if (value !== undefined) {
      targetMap['wpml:waypointHeadingParam.wpml:' + childName] = value
    }
  })
  KMZ_HEADING_GLOBAL_CHILDREN.forEach((childName) => {
    const value = globalResolver(childName)
    if (value !== undefined) {
      targetMap['wpml:globalWaypointHeadingParam.wpml:' + childName] = value
    }
  })
  return targetMap
}

export {
  buildKmzScopedHeadingPropsFromSources,
  applyKmzScopedHeadingProps,
  syncKmzPointHeadingScopesFromXml,
  syncKmzMissionHeadingScopesFromXml,
  isKmzHeadingFieldPath,
  isKmzGlobalHeadingFieldPath,
  resolveKmzPlaybackUseGlobalHeading,
  resolveKmzPlaybackLocalHeadingField,
  resolveKmzPlaybackGlobalHeadingField,
  resolveKmzTemplateExportUseGlobalHeading,
  resolveKmzTemplateExportHeadingField,
  resolveKmzWaylinesExportUseGlobalHeading,
  resolveKmzWaylinesExportHeadingField,
  applyKmzResolvedHeadingDisplayValuesToMap
}
